import { useState, useEffect, useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { backendService } from '../services/backendService';
import { isTestMode } from '../utils/testMode';

interface AuthState {
  backendHealthy: boolean | null;
}

interface AuthActions {
  handleLogout: () => void;
}

/**
 * Simplified auth hook - just handles backend health and logout
 * No more modals - Wallet component handles its own auth with overlay
 */
export const useAuth = (): AuthState & AuthActions => {
  const { logout } = usePrivy();
  
  // Just track backend health
  const [backendHealthy, setBackendHealthy] = useState<boolean | null>(null);

  // Check backend health on mount
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const healthy = await backendService.checkHealth();
        setBackendHealthy(healthy);
        
        if (!healthy) {
          console.error('Backend is not healthy');
        }
      } catch (error) {
        console.error('Failed to check backend health:', error);
        setBackendHealthy(false);
      }
    };

    checkBackend();
  }, []);

  // Handle test mode - just enable it, no UI changes needed
  useEffect(() => {
    if (isTestMode()) {
      console.log('🧪 Test mode enabled');
      setBackendHealthy(true);
    }
  }, []);

  // Handle user logout - reset everything
  const handleLogout = useCallback(() => {
    console.log('🚪 Starting logout process...');
    
    // Clear any app-specific localStorage items
    // Note: Privy manages its own localStorage, but we clear our app data
    try {
      localStorage.removeItem('test-mode'); // Clear test mode if active
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
    
    // Call Privy logout (this will cause page refresh due to OAuth)
    logout();
  }, [logout]);

  return {
    // State
    backendHealthy,
    
    // Actions
    handleLogout
  };
};